import { usePartner } from "./globalContext";

export type FormatType =
  | "currency"
  | "currencyThreeDecimal"
  | "smallCurrency"
  | "percentage"
  | "percentageThreeDecimal"
  | "addition"
  | "timestamp";
export type CustomFormatOptions = {
  formatType?: FormatType;
  decimalPlaces?: number;
  showEmptyData?: boolean;
};

const formatCurrency = (value: number, decimals: number = 2) => {
  return `$${defaultFormatNumber(value, decimals)}`;
};

const formatSmallCurrency = (value: number, decimals: number = 3) => {
  return `$${defaultFormatNumber(value, decimals)}`;
};

const formatPercentage = (value: number, decimals: number = 2) => {
  let formatted = defaultFormatNumber(value, decimals);
  let split = formatted.split(".");
  if (split[1] === "00") {
    formatted = split[0];
  }
  return `${formatted}%`;
};

const formatAddition = (value: number) => {
  let formatted = defaultFormatNumber(value);
  if (value > 0) return `+${formatted}`;
  return formatted;
};

const formatTimestamp = (value: number) => {
  return new Date(value).toLocaleDateString();
}

const defaultFormatNumber = (value: number, digits: number = 0) => {
  return value.toFixed(digits).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

const defaultCustomFormatOptions: CustomFormatOptions = {
  showEmptyData: false,
  decimalPlaces: 0,
};

export type AnalyticsFormatFunction = (
  valueToFormat: string | number,
  analyticsType?: string,
  formatOptions?: CustomFormatOptions
) => string;

export const useAnalyticsFormatting = (): AnalyticsFormatFunction => {
  const { partner } = usePartner();

  return (
    valueToFormat: string | number,
    analyticsType?: string,
    formatOptions: CustomFormatOptions = defaultCustomFormatOptions
  ): string => {
    if (valueToFormat === "?") return "?";

    if (typeof valueToFormat === "string") {
      valueToFormat = Number(valueToFormat);
    }

    if (!valueToFormat || isNaN(valueToFormat)) valueToFormat = 0;

    if (valueToFormat === 0 && !formatOptions.showEmptyData) return "---";

    let formatTypeFromConfig = analyticsType
      ? partner?.typeFormatMappings[analyticsType]
      : null;
    let formatType = formatTypeFromConfig ?? formatOptions.formatType;

    switch (formatType) {
      case "currency":
        return formatCurrency(valueToFormat);
      case "currencyThreeDecimal":
        return formatCurrency(valueToFormat, 3);
      case "smallCurrency":
        return formatSmallCurrency(valueToFormat);
      case "percentage":
        return formatPercentage(valueToFormat);
      case "percentageThreeDecimal":
        return formatPercentage(valueToFormat, 3)
      case "addition":
        return formatAddition(valueToFormat);
      case "timestamp":
        return formatTimestamp(valueToFormat);
      default:
        return defaultFormatNumber(valueToFormat, formatOptions.decimalPlaces);
    }
  };
};
